import React from 'react';
import {
  Avatar,
  Box,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Text,
} from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiChevronDown, FiExternalLink, FiLogOut, FiMenu } from 'react-icons/fi';
import useAdminSession from '../../hooks/useAdminSession';
import logoutAndRedirect from '../../lib/adminLogout';
import deriveDisplayName from '../../lib/adminDisplayName';
import ADMIN_NAV_ITEMS from './nav';
import { adminLayout } from '../../../theme/foundations/adminPanel';

/** Alto fijo de la topbar, igual en todos los anchos. */
export const TOPBAR_HEIGHT = adminLayout.topbarHeight;

/**
 * Topbar del panel: a la izquierda el título de la sección actual
 * (resuelto contra `ADMIN_NAV_ITEMS` por la ruta activa), a la derecha
 * únicamente el menú de cuenta. Sin buscador global, sin campana de
 * notificaciones y sin candado de sesión: eran controles decorativos
 * que no hacían nada real, y se eliminaron junto con su código y sus
 * pruebas en vez de quedar deshabilitados a medias.
 */
export default function AdminTopbar({ onOpenMobileMenu }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { email } = useAdminSession();

  const displayName = deriveDisplayName(email) || 'Administrador';
  const sectionTitle =
    ADMIN_NAV_ITEMS.find(item => item.href === pathname)?.label ||
    'Panel administrativo';

  const handleLogout = async () => {
    await logoutAndRedirect(navigate);
  };

  return (
    <Flex
      as="header"
      align="center"
      justify="space-between"
      gap={4}
      h={TOPBAR_HEIGHT}
      flexShrink={0}
      px={{ base: adminLayout.contentPaddingMobile, lg: adminLayout.contentPadding }}
      bg="white"
      borderBottom="1px solid"
      borderColor="adminPanel.topbarBorder"
      minW={0}
    >
      <Flex align="center" gap={3} minW={0}>
        <IconButton
          aria-label="Abrir menú de navegación"
          title="Abrir menú de navegación"
          icon={<FiMenu aria-hidden="true" />}
          variant="ghost"
          onClick={onOpenMobileMenu}
          display={{ base: 'inline-flex', lg: 'none' }}
          minW="44px"
          minH="44px"
          flexShrink={0}
        />
        {/* `minW={0}` es imprescindible aquí: sin él, un flex item nunca
            se encoge por debajo de su ancho de contenido, y en viewports
            angostos ese desbordamiento es justo lo que produce scroll
            horizontal. `noOfLines` ya aporta el recorte con "…". */}
        <Text
          as="h1"
          fontSize="20px"
          fontWeight={700}
          color="adminPanel.text"
          noOfLines={1}
          minW={0}
        >
          {sectionTitle}
        </Text>
      </Flex>

      <Menu placement="bottom-end">
        <MenuButton
          aria-label={`Cuenta de ${displayName}`}
          display="flex"
          alignItems="center"
          gap={2}
          minH="44px"
          px={2}
          borderRadius="8px"
          _hover={{ bg: 'adminPanel.bg' }}
          _focusVisible={{ boxShadow: 'outline' }}
        >
          <Flex align="center" gap={2}>
            <Avatar
              boxSize="34px"
              name={displayName}
              bg="adminPanel.navy"
              color="white"
            />
            <Box display={{ base: 'none', md: 'block' }} textAlign="left" maxW="200px" minW={0}>
              <Text
                fontSize="14px"
                fontWeight={600}
                noOfLines={1}
                color="adminPanel.text"
              >
                {displayName}
              </Text>
              <Text fontSize="13px" color="adminPanel.textMuted" noOfLines={1}>
                Administrador
              </Text>
            </Box>
            <Box
              as={FiChevronDown}
              aria-hidden="true"
              color="adminPanel.textMuted"
            />
          </Flex>
        </MenuButton>
        <MenuList w="190px" minW="190px" p="6px" borderRadius="10px">
          <Box px={3} py={2} maxW="190px">
            <Text fontSize="14px" fontWeight={600} noOfLines={1}>
              {displayName}
            </Text>
            <Text fontSize="13px" color="adminPanel.textMuted" noOfLines={1}>
              {email || 'Cargando…'}
            </Text>
          </Box>
          <MenuDivider />
          <MenuItem
            as="a"
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            icon={<FiExternalLink aria-hidden="true" />}
            h="38px"
            fontSize="14px"
            borderRadius="7px"
          >
            Ver sitio público
          </MenuItem>
          <MenuItem
            onClick={handleLogout}
            icon={<FiLogOut aria-hidden="true" />}
            h="38px"
            fontSize="14px"
            borderRadius="7px"
          >
            Cerrar sesión
          </MenuItem>
        </MenuList>
      </Menu>
    </Flex>
  );
}
